import { useCallback, useState } from 'react'
import * as Sentry from '@sentry/react'
import { ContentViewerActions } from '@/content-viewer/store/slice'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { selectLoadedUserFilesById } from '@/user_files/files/store/selectors'
import { MedChronSourceProvider, type MedChronSourceRef } from './schemas'
import { openMedChronSourceViewer, type OpenMedChronSourceViewerOptions } from './open-med-chron-source-viewer'

type SourceLoadError = {
  sourceId: string
  message: string
} | null

function buildMedChronOpenTelemetry(source: MedChronSourceRef, options: OpenMedChronSourceViewerOptions) {
  return {
    tags: {
      contentViewerFeature: 'medical_chronology',
      sourceProvider: source.provider,
      referenceBacked: String(Boolean(options.legacyReference)),
    },
    extra: {
      sourceUserFileId: source.provider === MedChronSourceProvider.USER_FILES ? source.source_id : null,
      legacyReferenceId: options.legacyReference?.id ?? null,
    },
  }
}

export function useOpenMedChronSource() {
  const dispatch = useAppDispatch()
  const loadedUserFilesById = useAppSelector(selectLoadedUserFilesById)
  const [sourceLoadError, setSourceLoadError] = useState<SourceLoadError>(null)

  const openMedChronSource = useCallback(
    async (source: MedChronSourceRef, options: OpenMedChronSourceViewerOptions = {}) => {
      setSourceLoadError(null)

      try {
        const result = await openMedChronSourceViewer({
          source,
          options,
          loadedUserFilesById,
          dispatch,
        })

        if (!result.opened) {
          const telemetry = buildMedChronOpenTelemetry(source, options)
          Sentry.captureMessage('Medical chronology source viewer open failed', {
            level: 'warning',
            tags: telemetry.tags,
            extra: {
              ...telemetry.extra,
              errorMessage: result.error,
            },
          })
          setSourceLoadError({ sourceId: source.source_id, message: result.error })
          return false
        }

        dispatch(ContentViewerActions.setContentViewerSession({ session: result.session }))
        return true
      } catch (error) {
        Sentry.captureException(error, buildMedChronOpenTelemetry(source, options))
        setSourceLoadError({ sourceId: source.source_id, message: 'Could not load this source file.' })
        return false
      }
    },
    [dispatch, loadedUserFilesById]
  )

  return {
    openMedChronSource,
    sourceLoadError,
  }
}
